import { useEffect, useState, useRef } from "react";
import axios from "../../../config/axios";

/* ============================================================
   BeaconPairingCode — pairing screen shown when the beacon addon
   exists but no Magic Beacon is paired to this account yet.
   Logic: 20s poll (slides server TTL — code never stale),
   409 = paired → onPaired/reload. See earlier notes.
   Styling: matches the Magic Beacon brand language — deep navy
   gradient, beacon glow, emerald accents (#05f651 family).
   ============================================================ */

const POLL_MS = 2 * 1000;

const BeaconPairingCode = ({ displayDeviceId, isTv = false, onPaired }) => {
  const [code, setCode] = useState(null);
  const [stale, setStale] = useState(false);
  const timerRef = useRef(null);
  const onPairedRef = useRef(onPaired);
  onPairedRef.current = onPaired;

  useEffect(() => {
    if (!displayDeviceId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const r = await axios.post(
          "/esp/pairing-code",
          { displayDeviceId },
          { withCredentials: true }
        );
        if (cancelled) return;
        setCode(r.data.pairingCode);
        setStale(false);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 409) {
          // Beacon exists → trigger the parent refetch, but KEEP polling.
          // If the flip succeeds we unmount and the interval dies with us;
          // if the refetch failed or returned stale data, the next poll
          // retries — no dead-end state.
          console.log("[pairing poll] 409 → paired, triggering refetch");
          if (onPairedRef.current) onPairedRef.current();
          return;
        }
        setStale(true);
        console.error("Pairing code poll failed:", e);
      }
    };

    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
    };
  }, [displayDeviceId]);

  const s = isTv
    ? { h: 52, p: 28, digits: 120, gap: 20, digitPad: "14px 30px", radius: 20 }
    : { h: 30, p: 17, digits: 68, gap: 12, digitPad: "8px 18px", radius: 14 };

  return (
    <div className={`bpc-wrap ${isTv ? "bpc-tv" : ""}`}>
      <style>{`
        .bpc-wrap{
          position:relative; display:flex; flex-direction:column;
          align-items:center; justify-content:center;
          padding-top:6vh;
          width:100%; height:100vh; box-sizing:border-box;
          text-align:center; overflow:hidden;
          font-family:"Lato",sans-serif;
          background:linear-gradient(180deg,#0b1e3f 0%,#123a7a 55%,#17448f 100%);
          padding:40px;
        }
        .bpc-tv{ padding:60px; padding-bottom:190px; }
        /* beacon burst behind the code */
        .bpc-wrap::before{
          content:""; position:absolute; left:50%; top:50%;
          width:140vmin; height:140vmin; transform:translate(-50%,-50%);
          background:conic-gradient(from 0deg,
            rgba(5,246,81,0) 0deg, rgba(5,246,81,0.05) 12deg, rgba(5,246,81,0) 24deg,
            rgba(255,255,255,0) 90deg, rgba(255,255,255,0.05) 102deg, rgba(255,255,255,0) 114deg,
            rgba(5,246,81,0) 180deg, rgba(5,246,81,0.05) 192deg, rgba(5,246,81,0) 204deg,
            rgba(255,255,255,0) 270deg, rgba(255,255,255,0.05) 282deg, rgba(255,255,255,0) 294deg);
          animation:bpc-spin 24s linear infinite; pointer-events:none;
        }
        @keyframes bpc-spin{ to{ transform:translate(-50%,-50%) rotate(360deg);} }

        .bpc-title{
          position:relative; margin:0 0 14px; font-weight:900; color:#fff;
          text-shadow:0 2px 12px rgba(0,0,0,0.35);
        }
        .bpc-sub{
          position:relative; margin:0; color:#c9d6f2; line-height:1.5; max-width:760px;
        }
        .bpc-sub strong{ color:#fff; }

        .bpc-steps{
          position:relative; list-style:none; margin:10px 0 0; padding:0;
          display:flex; flex-direction:column; gap:.55em;
          color:#c9d6f2; line-height:1.4; text-align:left;
        }
        .bpc-steps strong{ color:#fff; }
        .bpc-num{
          display:inline-flex; align-items:center; justify-content:center;
          width:1.6em; height:1.6em; margin-right:.55em;
          border-radius:50%; flex:0 0 auto;
          background:rgba(5,246,81,.18);
          border:2px solid rgba(5,246,81,.55);
          color:#fff; font-weight:900; font-size:.85em;
        }
        .bpc-steps li{ display:block; }
        .bpc-num{ vertical-align:middle; }

        .bpc-digits{
          position:relative; display:flex; margin-top:34px;
          font-weight:900; color:#fff; letter-spacing:2px;
          transition:opacity .4s ease;
        }
        .bpc-digit{
          background:rgba(255,255,255,0.10);
          border:3px solid rgba(5,246,81,0.55);
          box-shadow:0 0 22px rgba(5,246,81,0.28),
                     inset 0 1px 0 rgba(255,255,255,0.18);
          animation:bpc-breathe 3.2s ease-in-out infinite;
        }
        @keyframes bpc-breathe{
          0%,100%{ box-shadow:0 0 16px rgba(5,246,81,0.22), inset 0 1px 0 rgba(255,255,255,0.18); }
          50%   { box-shadow:0 0 30px rgba(5,246,81,0.45), inset 0 1px 0 rgba(255,255,255,0.18); }
        }

        .bpc-hint{
          position:relative; color:#8fa5cf; margin:28px 0 0; max-width:760px; line-height:1.5;
        }
        .bpc-stale{ position:relative; color:#fbbf24; font-weight:700; margin:18px 0 0; }
        .bpc-loading{ position:relative; color:#c9d6f2; margin-top:34px; }
      `}</style>

      <h2 className="bpc-title" style={{ fontSize: Math.round(s.h * 1.3) }}>
        Connect Your Magic Beacon Device!
      </h2>
      <p className="bpc-sub" style={{ fontSize: Math.round(s.h * 1.3), marginBottom: 10, maxWidth: "none" }}>
        Proceed to <strong>Step 3</strong> in your Beacon Quick Start Guide
      </p>
      <ol className="bpc-steps" style={{ fontSize: Math.round(s.p * 1.4) }}>
        <li><span className="bpc-num">1</span>Plug in your Magic Beacon Device</li>
        <li><span className="bpc-num">2</span>Join the{" "}<strong>MagicBeacon</strong>{" "}Wi-Fi network on your phone</li>
        <li><span className="bpc-num">3</span>Select your home Wi-Fi network</li>
        <li><span className="bpc-num">4</span>Enter this pairing code:</li> 
      </ol>

      {code ? (
        <div
          className="bpc-digits"
          style={{ gap: s.gap, fontSize: s.digits, opacity: stale ? 0.35 : 1 }}
        >
          {String(code)
            .split("")
            .map((d, i) => (
              <span
                key={i}
                className="bpc-digit"
                style={{
                  padding: s.digitPad,
                  borderRadius: s.radius,
                  animationDelay: `${i * 0.15}s`,
                }}
              >
                {d}
              </span>
            ))}
        </div>
      ) : (
        <p className="bpc-loading" style={{ fontSize: s.p }}>
          {stale ? "Reconnecting…" : "Getting your code…"}
        </p>
      )}

      {code && stale && (
        <p className="bpc-stale" style={{ fontSize: s.p }}>
          Reconnecting…
        </p>
      )}

     
    </div>
  );
};

export default BeaconPairingCode;
